import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logoZona from "../../assets/images/icons/logo.svg";
import "./BrandBook.scss";

// Shown in the top notice and the colophon. Change it with every content update.
const LAST_UPDATED = "2026-10-17";

// Last Call specimen: ticks like the app tile down to 0:00, then starts a fresh hour. Its own
// component so the once-a-second state doesn't re-render the whole page.
const LastCallTile = () => {
  // Below zero is the hold: 0:00 with the bar empty for HOLD_S seconds, then a fresh hour.
  const HOLD_S = 3;
  const [tick, setTick] = useState(42 * 60 + 17);
  useEffect(() => {
    const id = setInterval(() => setTick((s) => (s <= -HOLD_S ? 3600 : s - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, tick);
  return (
    <div className="bb-lastcall">
      <div className="bb-flyer__art" />
      <div className="bb-flyer__fade" />
      <div className="bb-lastcall__label">
        Last call · {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
      </div>
      <div className="bb-lastcall__meta">
        <div className="bb-lastcall__title">Main room table at Rooftop Saturday</div>
        <div className="bb-lastcall__detail">Tonight · $3,000 minimum</div>
      </div>
      <div className="bb-lastcall__bar" style={{ width: `${(left / 3600) * 100}%` }} />
    </div>
  );
};

// The Drop as the app's state screens draw it (assets/icons/drop.png).
const DropPin = ({ size }) => (
  <svg width={size} height={size} viewBox="16 12 48 58" aria-hidden="true">
    <defs>
      <linearGradient id={`bbDropPin${size}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#7229F5" />
        <stop offset="0.55" stopColor="#4F30EA" />
        <stop offset="1" stopColor="#0A3FD6" />
      </linearGradient>
    </defs>
    <path d="M40 14 C28 14 19 23 19 35 C19 51 40 67 40 67 C40 67 61 51 61 35 C61 23 52 14 40 14 Z" fill={`url(#bbDropPin${size})`} />
    <circle cx="40" cy="35" r="9" fill="#0A0A0A" />
    <circle cx="40" cy="35" r="4" fill="#C6FF3C" />
  </svg>
);

const BrandBook = () => {
  const navigate = useNavigate();
  // Wait for the title faces so the PDF doesn't print fallback fonts.
  const handleExport = async () => {
    if (document.fonts?.ready) await document.fonts.ready;
    window.print();
  };

  const palette = [
    {
      name: "Canvas",
      hex: "#050505",
      rgb: "5, 5, 5",
      pantone: "Black 6 C",
      use: "The dark canvas is the brand. Every surface starts here. Also the label and spinner color on lime.",
    },
    {
      name: "Brand Primary",
      hex: "#4F30EA",
      rgb: "79, 48, 234",
      pantone: "2725 C",
      use: "Interactive accents. Links, focus rings, tab underlines, action icons. The fill of Purple buttons and the shared Button's single-color fallback.",
    },
    {
      name: "Hero Purple",
      hex: "#7229F5",
      rgb: "114, 41, 245",
      pantone: "2665 C",
      use: "Hero accents. End stop of the gradient. Glow.",
    },
    {
      name: "Gradient Blue",
      hex: "#0A3FD6",
      rgb: "10, 63, 214",
      pantone: "2728 C",
      use: "Starting stop of the gradient. Charts, data accents.",
    },
    {
      name: "Radar Lime",
      hex: "#C6FF3C",
      rgb: "198, 255, 60",
      pantone: "381 C",
      light: true,
      use: "The night: the one action per screen that gets someone out or connects them, with a #050505 label. Also the default live signal: radar pins, DJ guest lists, verified badges and checkmarks. That use stays.",
    },
    {
      name: "Dark Surface",
      hex: "#1C1C1E",
      rgb: "28, 28, 30",
      pantone: "Not assigned",
      use: "Dark buttons with a white label: the alternative next to a main action, like Log in, Message, Add photos and Decline. Always dark, never grey. Disabled buttons and row fills.",
    },
    {
      name: "Countdown Red",
      hex: "#FF3B30",
      rgb: "255, 59, 48",
      pantone: "Bright Red C (nearest)",
      use: "Functional only. Actions that can't be undone: Log out, Delete, Remove, Refund (the Refund and Refunded button on the bottle service page was approved 2026-10-15). Also the system destructive color on Delete group, Leave group and Remove from guest list text. Never decorative. The Last Call tile stopped using it on 2026-10-16; its countdown is lime now.",
    },
  ];

  const typeScale = [
    { label: "Display", weight: 800, size: 88, role: "Manifestos. Cover. Marketing only." },
    { label: "H1", weight: 700, size: 52, role: "Section openers." },
    { label: "H2", weight: 700, size: 34, role: "Sub-headers." },
    { label: "H3", weight: 600, size: 22, role: "Card titles, callouts." },
    { label: "Lede", weight: 500, size: 19, role: "Intro paragraphs." },
    { label: "Body", weight: 400, size: 15, role: "Default running text." },
    { label: "Caption", weight: 500, size: 11, role: "Labels and meta. Sentence case." },
  ];

  // App type scale, approved 2026-10-15. This page is the source of truth.
  const appTypeScale = [
    { label: "Display", size: 40, weight: 800, sample: "Simone Adeyemi", use: "Profile name. Hero titles on event, table, plan and pass pages, 2 lines max, shrinks to 0.7 to fit." },
    { label: "Title", size: 22, weight: 700, sample: "You're on the list", use: "Page headlines on flow steps, gates and success screens. Big numbers like code digits and balances." },
    { label: "Headline", size: 18, weight: 700, sample: "Messages", use: "Screen titles, sheet and modal titles, section headings, card titles, empty state titles." },
    { label: "Body strong", size: 15, weight: 600, sample: "Do Not Sit On The Furniture", use: "Names in lists, prices in rows, tabs, text buttons. Chips and toggles are 13 / 600." },
    { label: "Body", size: 15, weight: 400, sample: "Miami for the music, New York for the people. Usually wherever the DJ is good.", use: "Body text, bio, chat messages, message previews, subtitles, menu rows, inputs." },
    { label: "Secondary", size: 13, weight: 400, sample: "Today · 9:41 PM", gray: true, use: "Secondary lines, times and dates, field labels, errors, notices, fine print." },
    { label: "Caption", size: 11, weight: 600, sample: "2", badge: true, use: "Small labels, badges and counts, tab bar labels." },
  ];

  // Button roles and sizes, approved 2026-10-14 and 2026-10-15. Shapes and the five colors, 2026-10-17.
  const buttonRoles = [
    { name: "Primary", cls: "bb-btn--lime", label: "Join", fill: "#C6FF3C fill · #050505 label" },
    { name: "Purple", cls: "bb-btn--purple", label: "Get ticket", fill: "#4F30EA fill · white label" },
    { name: "Secondary", cls: "bb-btn--dark", label: "Message", fill: "#1C1C1E fill · white label · always dark, never grey" },
    { name: "Red", cls: "bb-btn--red", label: "Remove", fill: "#FF3B30 fill · white label · only what can't be undone" },
  ];

  const buttonSizes = [
    { name: "Large", cls: "bb-btn--action", main: "Sign up", mainCls: "bb-btn--lime", alt: "Log in", spec: "height 50 · square, radius 12 · padding 20 · label 18 / 600 · icon 20, gap 8 · full width, pairs split the row, gap 10" },
    { name: "Medium", cls: "bb-btn--medium", main: "Try again", mainCls: "bb-btn--lime", alt: null, spec: "height 44 · pill, radius 22 · padding 16 · label 15 / 600 · icon 18, gap 8 · pairs equal width, alone hugs the label, min 120" },
    { name: "Small", cls: "bb-btn--small bb-btn--list", main: "Accept", mainCls: "bb-btn--purple", alt: "Decline", spec: "height 38 · pill, radius 19 · padding 14 · label 15 / 600 · icon 16, gap 6 · alone hugs the label, min 88 · in a list or pair, width 108" },
  ];

  // The five kinds of button color, approved 2026-10-17. No grey.
  const buttonColors = [
    { cls: "bb-btn--lime", label: "Join", name: "Lime is the main action", use: "The one main action of the night: join, request a table, send a drink, Sign up, Done on Rate your table. One per screen." },
    { cls: "bb-btn--purple", label: "See tables", name: "Purple is other actions", use: "Other actions and purchases: See tables, Get ticket, Accept, Vouchers, Redeem." },
    { cls: "bb-btn--dark", label: "Log in", name: "Dark is the alternative", use: "The alternative next to a main action: Log in, Message, Add photos, Decline. Always dark #1C1C1E, never grey." },
    { cls: "bb-btn--ghost", label: "Not now", name: "Text only is backing out", use: "No fill, for backing out: Cancel, Not now, Skip." },
    { cls: "bb-btn--red", label: "Remove", name: "Red means it can't be undone", use: "#FF3B30, only for what can't be undone: Log out, Delete, Remove, Refund." },
  ];

  // Inter ships inside the app in every weight (Zona/plugins/withInterFont.js).
  const interWeights = [300, 400, 500, 600, 700, 800, 900];

  // Plan and invite titles. Fixed catalog: zona-superadmin-server/src/scripts/planThemes/catalogFonts.js
  const titleFonts = [
    { name: "Bold", face: "Inter Black", family: "'Inter', sans-serif", weight: 900, italic: false },
    { name: "Poster", face: "Anton", family: "'Anton', sans-serif", weight: 400, italic: false },
    { name: "Wide", face: "Michroma", family: "'Michroma', sans-serif", weight: 400, italic: false },
    { name: "Couture", face: "Bodoni Moda Bold Italic", family: "'Bodoni Moda', serif", weight: 700, italic: true },
    { name: "Editorial", face: "Instrument Serif Italic", family: "'Instrument Serif', serif", weight: 400, italic: true },
    { name: "Disco", face: "Monoton", family: "'Monoton', sans-serif", weight: 400, italic: false },
    { name: "Neon", face: "Neonderthaw", family: "'Neonderthaw', cursive", weight: 400, italic: false },
    { name: "Gothic", face: "Pirata One", family: "'Pirata One', serif", weight: 400, italic: false },
    { name: "Techno", face: "Major Mono Display", family: "'Major Mono Display', monospace", weight: 400, italic: false },
  ];

  // Title faces load only while this page is open, for the specimens.
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Anton&family=Michroma&family=Bodoni+Moda:ital,wght@1,700" +
      "&family=Instrument+Serif:ital@1&family=Monoton&family=Neonderthaw&family=Pirata+One" +
      "&family=Major+Mono+Display&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);


  // The live radar speaks in pins: color is the state, the glyph is the offer.
  // Paths mirror Zona/src/components/PulsingMarker/PulsingMarker.tsx exactly.
  // Lime is a light fill, so its glyph is dark; red / amber carry a white glyph.
  const LIME = "#C6FF3C";
  const AMBER = "#E67E22";
  const RED = "#E11D48";
  const radarGlyphs = [
    {
      name: "bottle",
      label: "Bottle service",
      use: "Tables and bottle offers at the venue.",
      dot: LIME,
      path: "M10 2h4v2h-1v3l1.5 3.2v8.3a2.5 2.5 0 0 1-2.5 2.5h-2a2.5 2.5 0 0 1-2.5-2.5v-8.3L9 7V4H8V2z",
    },
    {
      name: "restaurant",
      label: "Dinner",
      use: "Dining reservations. Amber marks a dinner-only venue and matches the dinner card.",
      dot: AMBER,
      path: "M8.1 13.34l2.83-2.83L3.91 3.5a4 4 0 0 0 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z",
    },
    {
      name: "ticket",
      label: "Tickets",
      use: "Ticketed entry on sale, including a guest list that sells tiers.",
      dot: LIME,
      path: "M22 10V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4a2 2 0 0 1 0 4v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 1 0-4zm-7 7h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z",
    },
    {
      name: "people",
      label: "Guest list",
      use: "Open guest list, no tickets.",
      dot: LIME,
      path: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4 0-7 2-7 5v2h14v-2c0-3-3-5-7-5zm8.5-1a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm.5 2c-1 0-2 .2-2.7.6 1.6 1.2 2.7 2.8 2.7 4.4v1H22v-1c0-2.5-2-5-4-5z",
    },
    {
      name: "headphones",
      label: "DJ",
      use: "A member plan hosted by a Verified DJ. DJ wins when a host is both.",
      dot: LIME,
      path: "M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z",
    },
    {
      name: "star",
      label: "Promoter",
      use: "A Verified Promoter hosts this plan. Shipped 2026-09-09.",
      dot: LIME,
      path: "M12 2l2.9 6.26 6.85.72-5.11 4.6 1.45 6.72L12 17.6l-6.09 2.7 1.45-6.72-5.11-4.6 6.85-.72z",
    },
    {
      name: "plan",
      label: "Member plan",
      use: "Any other member's plan.",
      dot: LIME,
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z",
    },
    {
      name: "hourglass",
      label: "Last call",
      use: "Closing soon. Wins over everything: the pin turns red and pulses faster.",
      dot: RED,
      path: "M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6z",
    },
    {
      name: "lock",
      label: "Locked",
      use: "Access-gated: the venue is on the radar with no open offer yet.",
      dot: LIME,
      path: "M7 10V8a5 5 0 1 1 10 0v2h.5A1.5 1.5 0 0 1 19 11.5v8A1.5 1.5 0 0 1 17.5 21h-11A1.5 1.5 0 0 1 5 19.5v-8A1.5 1.5 0 0 1 6.5 10H7zm2 0h6V8a3 3 0 1 0-6 0v2z",
    },
  ];

  return (
    <main className="brandbook-page">
      <article className="brandbook">
        <div className="brandbook__toolbar">
          <button
            className="brandbook__back"
            onClick={() => navigate("/")}
            aria-label="Back to admin"
          >
            <span className="iconify" data-icon="solar:arrow-left-outline" style={{ fontSize: 16 }}></span>
            <span>Admin</span>
          </button>
          <button className="brandbook__export" onClick={handleExport} aria-label="Export to PDF">
            <span className="iconify" data-icon="solar:download-outline" style={{ fontSize: 16 }}></span>
            <span>Export PDF</span>
          </button>
        </div>

          <div className="bb-notice bb-print-hide">
            This page is ZONA's one brand book, last updated {LAST_UPDATED}. Agents
            read it in Admin-panel-new at <code>src/pages/BrandBook/BrandBook.jsx</code>,
            and changes are made there.
          </div>

          {/*  ─────────────  COVER  ─────────────  */}
          <section className="bb-spread bb-cover">
            <div className="bb-cover__corner-tl">
              <div className="bb-eyebrow">ZONA</div>
              <div className="bb-mono bb-cover__edition">Vol. 01 · {new Date().getFullYear()}</div>
            </div>

            <div className="bb-cover__corner-br">
              <div className="bb-eyebrow">Brand guidelines</div>
              <div className="bb-mono">For internal &amp; partner use</div>
            </div>

            <div className="bb-cover__statement">
              <p className="bb-eyebrow">A manifesto</p>
              <h1 className="bb-display">
                Going out is supposed to feel like a moment.
                <span className="bb-display__accent"> We make it one.</span>
              </h1>
            </div>
          </section>

          {/*  ─────────────  MANIFESTO  ─────────────  */}
          <section className="bb-spread bb-manifesto">
            <header className="bb-section-head">
              <div className="bb-section-head__num">00</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">The brand</div>
                <h2 className="bb-h1">Who we are.</h2>
              </div>
            </header>

            <div className="bb-prose bb-prose--lead">
              <p>
                Zona is the front door to Miami nightlife. The heat-map of what's
                happening tonight, the line you can skip, the table you can claim,
                the friends who are already there.
              </p>
              <p>
                Our brand should feel like the city after midnight. Confident.
                Rare. Specific. Never generic, never trying too hard.
              </p>
              <p>
                Every surface, every email, every push notification carries the
                same purple, the same sharp typography, the same restraint.
                Consistency is the brand.
              </p>
            </div>
          </section>

          {/*  ─────────────  01 · VOICE  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">01</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Voice</div>
                <h2 className="bb-h1">Action-heavy. Specific. Never cliched.</h2>
                <p className="bb-lede">
                  We sound like a friend who knows the city, not a brand
                  trying too hard. Concrete over abstract. Verb-led.
                </p>
              </div>
            </header>

            <div className="bb-voice">
              <div className="bb-voice__col bb-voice__col--do">
                <div className="bb-voice__label">Do</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-voice__quote">"See tonight."</span>
                    <span className="bb-voice__note">Verb leads. Three words. Specific to the moment.</span>
                  </li>
                  <li>
                    <span className="bb-voice__quote">"Two tables left at LIV."</span>
                    <span className="bb-voice__note">Names the place. Gives a number. Creates urgency.</span>
                  </li>
                  <li>
                    <span className="bb-voice__quote">"Skip the line. Claim the table."</span>
                    <span className="bb-voice__note">Action verbs. Parallel structure.</span>
                  </li>
                </ul>
              </div>

              <div className="bb-voice__col bb-voice__col--dont">
                <div className="bb-voice__label">Don't</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-voice__quote">"Tonight's hottest events."</span>
                    <span className="bb-voice__note">Cliched superlative. No verb. Could be any app.</span>
                  </li>
                  <li>
                    <span className="bb-voice__quote">"Discover amazing nightlife."</span>
                    <span className="bb-voice__note">Empty adjective. Vague. Tries too hard.</span>
                  </li>
                  <li>
                    <span className="bb-voice__quote">"Click here to learn more."</span>
                    <span className="bb-voice__note">Generic CTA. Not specific. Not Zona.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bb-voice__rules">
              <div className="bb-rule">
                <div className="bb-rule__num">01</div>
                <p>No em dashes and no "--" anywhere people read. Use periods, commas, and, or or. The server test noEmDashes.test.js enforces it.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">02</div>
                <p>No emojis in product UI. Icons only.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">03</div>
                <p>Honest claims. Mock data is labelled "Example preview."</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">04</div>
                <p>Verb-led copy. Lead with what the user can do.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">05</div>
                <p>No exclamation marks. Confidence doesn't need one.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">06</div>
                <p>No "Hi there." Start with the thing.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">07</div>
                <p>Never "dashboard." Venues and partners hate the word.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">08</div>
                <p>Contractions are fine. "You're in" beats "You are in."</p>
              </div>
            </div>
          </section>

          {/*  ─────────────  02 · LOGO  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">02</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Logo</div>
                <h2 className="bb-h1">The Drop &amp; the wordmark.</h2>
                <p className="bb-lede">
                  Two marks. <strong>The Drop</strong>, our pin, carries the
                  brand in compact space: app icon, favicon, tab bar, the radar.
                  The <strong>ZONA wordmark</strong> anchors full lockups. The
                  slashed-O is retired (2026).
                </p>
              </div>
            </header>

            <div className="bb-logo-stage">
              <div className="bb-logo-stage__halo" />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 30, position: "relative" }}>
                <svg width="128" height="128" viewBox="0 0 80 80" aria-label="The Drop">
                  <defs>
                    <linearGradient id="bbDrop" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#7229F5" />
                      <stop offset="0.55" stopColor="#4F30EA" />
                      <stop offset="1" stopColor="#0A3FD6" />
                    </linearGradient>
                  </defs>
                  <path d="M40 14 C28 14 19 23 19 35 C19 51 40 67 40 67 C40 67 61 51 61 35 C61 23 52 14 40 14 Z" fill="url(#bbDrop)" />
                  <circle cx="40" cy="35" r="9" fill="#0A0A0A" />
                  <circle cx="40" cy="35" r="4" fill="#C6FF3C" />
                </svg>
                <img src={logoZona} alt="ZONA wordmark" className="bb-logo-stage__mark" />
              </div>
            </div>

            <div className="bb-grid-4">
              <div>
                <div className="bb-eyebrow">Clearspace</div>
                <p className="bb-prose">
                  Maintain at least 1× the cap height of the wordmark on every
                  side. Nothing enters this zone.
                </p>
              </div>
              <div>
                <div className="bb-eyebrow">Sizing</div>
                <p className="bb-prose">
                  26 to 30 pixels in product navigation. 40 pixels and above in
                  marketing. Never below 20 pixels.
                </p>
              </div>
              <div>
                <div className="bb-eyebrow">Color</div>
                <p className="bb-prose">
                  The wordmark is white on the canvas. The Drop is purple to blue
                  with a single Radar Lime spot, drawn with its own three stops
                  (#7229F5, #4F30EA at 55%, #0A3FD6), exactly as in the app icon.
                  That fill belongs to the mark only. Never recolor either.
                </p>
              </div>
              <div>
                <div className="bb-eyebrow">Don't</div>
                <p className="bb-prose">
                  No stretch, rotate, outline, drop-shadow, 3D, or insufficient
                  contrast. The wordmark is the wordmark.
                </p>
              </div>
            </div>
          </section>

          {/*  ─────────────  03 · COLOR  ─────────────  */}
          <section className="bb-spread bb-spread--no-margin">
            <header className="bb-section-head bb-section-head--inset">
              <div className="bb-section-head__num">03</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Color</div>
                <h2 className="bb-h1">A small palette, used with intent.</h2>
                <p className="bb-lede">
                  The signature is the gradient. The brand is the canvas. Everything
                  else supports.
                </p>
              </div>
            </header>

            {/*  Hero gradient at full bleed  */}
            <div className="bb-gradient-bleed">
              <div className="bb-gradient-bleed__bar" />
              <div className="bb-gradient-bleed__caption">
                <div className="bb-eyebrow">Signature</div>
                <h3 className="bb-h2">The gradient.</h3>
                <p>
                  Hero accents and the brand stamp: section numbers, display
                  accents, web and marketing CTAs. Always at 135 degrees.
                  Two stops, blue to purple. App buttons stopped using it on
                  2026-10-17: they are solid purple or lime now.
                  The Drop keeps its own three-stop fill (see Logo).
                </p>
                <code className="bb-code">linear-gradient(135deg, #0A3FD6 0%, #7229F5 100%)</code>
              </div>
            </div>

            {/*  Color blocks, full-bleed, one per row  */}
            <div className="bb-color-stack">
              {palette.map((c) => {
                const dark = !c.light;
                return (
                  <div
                    key={c.hex}
                    className={`bb-color-row ${dark ? "is-dark" : ""}`}
                    style={{ background: c.hex }}
                  >
                    <div className="bb-color-row__name">{c.name}</div>
                    <div className="bb-color-row__values">
                      <div><span className="bb-eyebrow">Hex</span><code>{c.hex}</code></div>
                      <div><span className="bb-eyebrow">RGB</span><code>{c.rgb}</code></div>
                      <div><span className="bb-eyebrow">Pantone</span><code>{c.pantone}</code></div>
                    </div>
                    <div className="bb-color-row__use">{c.use}</div>
                  </div>
                );
              })}
            </div>

            <div className="bb-section-head--inset">
              <div className="bb-subhead">
                <h3 className="bb-h2">Retired: Gradient Magenta.</h3>
                <p className="bb-prose">
                  Gradient Magenta #B73AFF was retired on 2026-07-04 and removed
                  from the brand everywhere, app and admin. The gradient used to
                  have three stops with magenta; it now has two. No magenta, ever.
                  Do not reintroduce it.
                </p>
              </div>
            </div>
          </section>

          {/*  ─────────────  04 · TYPE  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">04</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Type</div>
                <h2 className="bb-h1">Inter for the app. Eight faces for titles.</h2>
                <p className="bb-lede">
                  Every piece of app UI is Inter. Differentiation comes from weight
                  and size, never family. Plan and invite titles are the one place
                  another face appears, and that list is fixed. App screens use the
                  seven-step type scale in section 05.
                </p>
              </div>
            </header>

            <div className="bb-subhead">
              <h3 className="bb-h2">This book and marketing pages.</h3>
              <p className="bb-prose">
                The sizes below set this book and the web. They are not for app
                screens. For the app, use the type scale in section 05.
              </p>
            </div>

            <div className="bb-type-stack">
              {typeScale.map((t) => (
                <div className="bb-type-row" key={t.label}>
                  <div className="bb-type-row__meta">
                    <div className="bb-type-row__label">{t.label}</div>
                    <div className="bb-mono">{t.size}px · {t.weight}</div>
                    <div className="bb-type-row__role">{t.role}</div>
                  </div>
                  <div
                    className="bb-type-row__sample"
                    style={{ fontSize: `${t.size}px`, fontWeight: t.weight }}
                  >
                    Tonight in Miami.
                  </div>
                </div>
              ))}
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Bundled in the app, 300 to 900.</h3>
              <p className="bb-prose">
                Inter v4.1 ships inside the app in every weight from 300 to 900,
                plus italics from 300 to 600, so the right face draws from the
                first frame. Never load another face named Inter at runtime: it
                joins the system Inter family and can take over every Inter
                request.
              </p>
            </div>

            <div className="bb-weight-strip">
              {interWeights.map((w) => (
                <div className="bb-weight-strip__cell" key={w}>
                  <div className="bb-weight-strip__sample" style={{ fontWeight: w }}>Aa</div>
                  <div className="bb-mono">{w}</div>
                </div>
              ))}
            </div>

            <div className="bb-system-stack">
              <div className="bb-system-row"><span className="bb-system-row__role">Registered</span><code>Zona/plugins/withInterFont.js</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">App default</span><code>Zona/src/setup/defaultFont.ts</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">Inter guard</span><code>Zona/src/services/planFonts.js</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Plan and invite titles.</h3>
              <p className="bb-prose">
                Eight approved catalog faces plus the Bold classic title in Inter
                Black. Titles only, never UI. Nothing else is approved.
              </p>
            </div>

            <div className="bb-title-grid">
              {titleFonts.map((f) => (
                <div className="bb-title-cell" key={f.name}>
                  <div
                    className="bb-title-cell__sample"
                    style={{ fontFamily: f.family, fontWeight: f.weight, fontStyle: f.italic ? "italic" : "normal" }}
                  >
                    Rooftop Saturday
                  </div>
                  <div className="bb-title-cell__name">{f.name}</div>
                  <div className="bb-mono">{f.face}</div>
                </div>
              ))}
            </div>
          </section>

          {/*  ─────────────  05 · TYPE SCALE  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">05</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Type scale</div>
                <h2 className="bb-h1">Seven styles. Nothing in between.</h2>
                <p className="bb-lede">
                  Every text size in the app comes from these seven styles, all in
                  Inter. No screen types its own size.
                </p>
              </div>
            </header>

            <div className="bb-scale">
              {appTypeScale.map((t) => (
                <div className="bb-type-row" key={t.label}>
                  <div className="bb-type-row__meta">
                    <div className="bb-name">{t.label}</div>
                    <div className="bb-mono">{t.size} / {t.weight}</div>
                    <div className="bb-type-row__role">{t.use}</div>
                  </div>
                  <div className="bb-scale__specimen">
                    <span
                      className={
                        "bb-scale__sample" +
                        (t.gray ? " bb-scale__sample--gray" : "") +
                        (t.badge ? " bb-scale__sample--badge" : "")
                      }
                      style={{ fontSize: `${t.size}px`, fontWeight: t.weight }}
                    >
                      {t.sample}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bb-voice">
              <div className="bb-voice__col bb-voice__col--do">
                <div className="bb-voice__label">Do</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-scale__demo" style={{ fontSize: 15, fontWeight: 400 }}>
                      Miami for the music, New York for the people.
                    </span>
                    <span className="bb-voice__note">A bio in Body, 15 / 400.</span>
                  </li>
                  <li>
                    <span className="bb-voice__note">Pick one of the seven styles for every piece of text.</span>
                  </li>
                </ul>
              </div>

              <div className="bb-voice__col bb-voice__col--dont">
                <div className="bb-voice__label">Don't</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-scale__demo" style={{ fontSize: 17, fontWeight: 400 }}>
                      Miami for the music, New York for the people.
                    </span>
                    <span className="bb-voice__note">A 17pt bio. It's off the scale.</span>
                  </li>
                  <li>
                    <span className="bb-voice__note">Half sizes like 12.5, or a new size for one screen.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Font</span><code>Inter for all app UI</code></div>
              <div className="bb-spec-row"><span>Button labels</span><code>follow the button size: 18, 15 or 13 (see Buttons)</code></div>
              <div className="bb-spec-row"><span>Left as is</span><code>the table request countdown clock · avatar initials · icon glyphs · the wordmark · sizes the server controls</code></div>
              <div className="bb-spec-row"><span>Title fonts</span><code>plan and invite title faces are separate and fixed (see Type)</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-15, replacing 36 different sizes across 1,087 texts</code></div>
            </div>
          </section>

          {/*  ─────────────  06 · LABELS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">06</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Labels</div>
                <h2 className="bb-h1">Sentence case, everywhere.</h2>
                <p className="bb-lede">
                  Labels read like words, not stamps. Section labels are small,
                  bold and sentence case. No small, letter-spaced capitals and no
                  uppercase transforms on UI labels. That look reads as generic
                  template design, not ZONA.
                </p>
              </div>
            </header>

            <div className="bb-voice">
              <div className="bb-voice__col bb-voice__col--do">
                <div className="bb-voice__label">Do</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-label-sample">Requests 2</span>
                    <span className="bb-voice__note">The count sits in the label. Sentence case.</span>
                  </li>
                  <li>
                    <span className="bb-label-sample">Photos</span>
                    <span className="bb-voice__note">Small, bold, normal letter spacing.</span>
                  </li>
                  <li>
                    <span className="bb-label-sample">When</span>
                    <span className="bb-voice__note">One plain word is enough.</span>
                  </li>
                </ul>
              </div>

              <div className="bb-voice__col bb-voice__col--dont">
                <div className="bb-voice__label">Don't</div>
                <ul className="bb-voice__list">
                  <li>
                    <span className="bb-label-sample bb-label-sample--caps">Requests · 2</span>
                    <span className="bb-voice__note">Small, letter-spaced capitals. Reads as generic template design.</span>
                  </li>
                  <li>
                    <span className="bb-label-sample bb-label-sample--caps">Photos</span>
                    <span className="bb-voice__note">An uppercase transform on a UI label.</span>
                  </li>
                  <li>
                    <span className="bb-label-sample bb-label-sample--caps">When</span>
                    <span className="bb-voice__note">Tracking added to make a label look designed.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Applied</span><code>app-wide on 2026-10-14</code></div>
              <div className="bb-spec-row"><span>Exception</span><code>the flyer offering pills keep their uppercase words (see Flyer tiles)</code></div>
              <div className="bb-spec-row"><span>Exception</span><code>ON PARTNER SITE on the LIV and Gekko event row stays in capitals, her call on 2026-10-16 (see Screens)</code></div>
            </div>
          </section>

          {/*  ─────────────  07 · BUTTONS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">07</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Buttons</div>
                <h2 className="bb-h1">A role plus a size.</h2>
                <p className="bb-lede">
                  Every button is named like a text style: a role and a size. Send
                  a drink is Primary medium. Message is Secondary medium. Every
                  button uses the shared Button with a size, and no screen builds
                  its own or changes its height, radius or label size. Every button
                  decision is recorded here, so the app stays consistent.
                </p>
              </div>
            </header>

            <div className="bb-subhead">
              <h3 className="bb-h2">Roles.</h3>
            </div>

            <div className="bb-btn-grid">
              {buttonRoles.map((r) => (
                <div className="bb-btn-cell" key={r.name}>
                  <div className="bb-btn-cell__stage">
                    <button className={`bb-btn bb-btn--medium ${r.cls}`}>{r.label}</button>
                  </div>
                  <div className="bb-name">{r.name}</div>
                  <div className="bb-mono">{r.fill}</div>
                </div>
              ))}
              <div className="bb-btn-cell">
                <div className="bb-btn-cell__stage">
                  <button className="bb-btn bb-btn--text">See all</button>
                  <button className="bb-btn bb-btn--text bb-btn--text-muted">Not now</button>
                </div>
                <div className="bb-name">Text button</div>
                <div className="bb-mono">no fill · white to go somewhere (See all) · muted to back out (Cancel, Not now, Skip)</div>
              </div>
              <div className="bb-btn-cell">
                <div className="bb-btn-cell__stage">
                  <button className="bb-btn bb-btn--chip">Tonight</button>
                  <button className="bb-btn bb-btn--chip">This weekend</button>
                </div>
                <div className="bb-name">Chip</div>
                <div className="bb-mono">for selecting, not acting</div>
              </div>
              <div className="bb-btn-cell">
                <div className="bb-btn-cell__stage">
                  <div className="bb-toggle">
                    <span className="bb-toggle__seg bb-toggle__seg--on">Upcoming</span>
                    <span className="bb-toggle__seg">Past</span>
                  </div>
                </div>
                <div className="bb-name">Toggle</div>
                <div className="bb-mono">segmented, segments share the track</div>
              </div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Sizes.</h3>
            </div>

            <div className="bb-scale">
              {buttonSizes.map((z) => (
                <div className="bb-type-row" key={z.name}>
                  <div className="bb-type-row__meta">
                    <div className="bb-name">{z.name}</div>
                    <div className="bb-type-row__role">{z.spec}</div>
                  </div>
                  <div className="bb-btn-row bb-btn-row--specimen">
                    <button className={`bb-btn ${z.mainCls} ${z.cls}`}>{z.main}</button>
                    {z.alt && <button className={`bb-btn bb-btn--dark ${z.cls}`}>{z.alt}</button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Chip</span><code>height 34 · full pill, radius 17 · padding 14 · label 13 / 600 · icon 16, gap 6 · hugs the label</code></div>
              <div className="bb-spec-row"><span>Toggle</span><code>track 40 · radius 12, segments 10 · label 13 / 600 · segments flex 1</code></div>
              <div className="bb-spec-row"><span>Text button</span><code>tap target 44 · label 15 / 600 · icon 16, gap 6 · hugs, no fill · white to go somewhere (See all) · gray to back out (Not now, Skip)</code></div>
              <div className="bb-spec-row"><span>Default size</span><code>Large. Profile action pairs are Medium, kept square</code></div>
              <div className="bb-spec-row"><span>Shape</span><code>Large square, radius 12 · Medium pill, radius 22 · Small pill, radius 19 · profile actions square at 12 (see Shapes)</code></div>
              <div className="bb-spec-row"><span>Label sizes</span><code>from the type scale only: 18, 15, 13. No other label sizes</code></div>
              <div className="bb-spec-row"><span>Icons on lime</span><code>#050505, like the label and the loading spinner</code></div>
              <div className="bb-spec-row"><span>In code</span><code>{'<Button type="primary" size="medium">'}</code></div>
              <div className="bb-spec-row"><span>Component</span><code>Zona/src/components/Button</code></div>
              <div className="bb-spec-row"><span>Disabled</span><code>#1C1C1E fill · label 40% white</code></div>
              <div className="bb-spec-row"><span>Glass</span><code>every filled button carries the GlassSheen of its color (see Forms, Glass on buttons)</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Which color goes where.</h3>
              <p className="bb-prose">
                Five kinds, and no grey. The color says what kind of action it
                is, and the same action is the same color on every screen: Send a
                drink is lime on a profile and in chat. One lime action per
                screen; everything else is purple, dark, text only or red.
                Approved 2026-10-17.
              </p>
            </div>

            <div className="bb-btn-uses">
              {buttonColors.map((c) => (
                <div className="bb-btn-use" key={c.name}>
                  <div className="bb-btn-use__sample">
                    <button className={`bb-btn bb-btn--medium ${c.cls}`}>{c.label}</button>
                  </div>
                  <div>
                    <div className="bb-name">{c.name}</div>
                    <div className="bb-btn-use__text">{c.use}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Pairs</span><code>the main action and its dark alternative share size and shape (Sign up + Log in, Send a drink + Message, Edit profile + Add photos, Accept + Decline)</code></div>
              <div className="bb-spec-row"><span>Gradient</span><code>retired on buttons 2026-10-17. A Button with no type is solid purple; the signup steps are lime, since each is that screen's one main action. Never hand-roll a gradient on a button</code></div>
              <div className="bb-spec-row"><span>Exception</span><code>popups keep their buttons as they are: a grey Cancel box (10% white, 70% white label) and Log Out in red text on a red tint (#FF3B30 on 15% red). Their corners stay square at 12, the Large radius</code></div>
              <div className="bb-spec-row"><span>No grey</span><code>Look and Music (plan designer), Clear all, See venues near you (white label) and the signed-out Sign in are dark #1C1C1E with the secondary glass since 2026-10-17</code></div>
              <div className="bb-spec-row"><span>Open</span><code>Log in's Continue is purple; lime like the signup steps is her call. Non-button gradient spots (Wallet row in Settings, organizer approve circles, See all cards, the in-app banner) are her call too</code></div>
              <div className="bb-spec-row"><span>Labels</span><code>sentence case: Sign up, Log in, Join the guestlist. Never all caps</code></div>
              <div className="bb-spec-row"><span>Icons</span><code>actions are labeled buttons, never icon-only</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>sizes 2026-10-14 · roles 2026-10-15 · shapes and the five colors 2026-10-17</code></div>
              <div className="bb-spec-row"><span>Why</span><code>button audit 2026-10-14 on reachable screens: 301 buttons (140 shared Button, 157 hand-built, 4 legacy), 24 heights, 19 radii</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Shapes.</h3>
              <p className="bb-prose">
                Large stays square, radius 12. Medium is a pill, radius 22.
                Small is a pill, radius 19. One exception: the profile actions
                stay square at 12. Big full-width square buttons next to the
                pills elsewhere give the screen dimension. Decided 2026-10-17,
                replacing the squarer Medium 12 and Small 10 from Zona #498.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-btn-row bb-btn-row--specimen">
                <button className="bb-btn bb-btn--lime bb-btn--medium bb-btn--square bb-btn--action">Send a drink</button>
                <button className="bb-btn bb-btn--dark bb-btn--medium bb-btn--square bb-btn--action">Message</button>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Square at 12</span><code>Send a drink · Message · Message {'{name}'} · Edit profile · Add photos</code></div>
              <div className="bb-spec-row"><span>In code</span><code>{'<Button size="medium" square>'} · square takes the Large radius</code></div>
              <div className="bb-spec-row"><span>Everything else</span><code>Medium and Small are pills · popups stay square at 12 (see Which color goes where)</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Small pills in a list.</h3>
              <p className="bb-prose">
                Small pills in a list or a pair share one width, 108, so their
                edges line up down the page. A Small pill on its own hugs its
                label, min 88. Every label has to fit 108 at 15 / 600, so long
                labels were shortened: Join the table became Join, Redeem
                voucher became Redeem, and View vouchers became Vouchers.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-people">
                <div className="bb-people__row">
                  <span className="bb-people__avatar" />
                  <span className="bb-people__text bb-people__name">Maya Torres</span>
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">Accept</button>
                </div>
                <div className="bb-people__row">
                  <span className="bb-people__avatar" />
                  <span className="bb-people__text bb-people__name">Sam Okafor</span>
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">Unblock</button>
                </div>
                <div className="bb-people__row">
                  <span className="bb-people__avatar" />
                  <span className="bb-people__text bb-people__name">Priya Raman</span>
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">Join</button>
                </div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>List width</span><code>108 · BUTTON_SIZES.small.listWidth · label on one line, 4 inset each side, truncates rather than wraps</code></div>
              <div className="bb-spec-row"><span>Alone</span><code>hugs the label · padding 14 · min 88</code></div>
              <div className="bb-spec-row"><span>Where</span><code>guest list Accept and Decline · table invites Join and Approve · message requests Accept · notifications Accept · Blocked Unblock · joinable tables Ask to join · event page offer rows</code></div>
              <div className="bb-spec-row"><span>Shortened</span><code>Join the table → Join · Redeem voucher → Redeem · in other languages: Comprar (es, pt) and Acheter (fr) for Get ticket, Resgatar (pt), Canjear (es) and Utiliser (fr) for Redeem, Voir les bons (fr) and Vouchers (en) for View vouchers · since 2.0.52: 38 tall, 15 / 600, width 108 (was 34, 13 / 600, 96), her call 2026-10-17</code></div>
              <div className="bb-spec-row"><span>In code</span><code>{'<Button size="small" inList>'}</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">On the web.</h3>
              <p className="bb-prose">
                On the web panels and marketing pages, the gradient pill is the
                primary action.
              </p>
            </div>

            <div className="bb-button-stage">
              <button className="bb-cta">Get started</button>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Web · pill</span><code>2-stop gradient · background-size 180% · radius 100px · padding 16px 26px</code></div>
              <div className="bb-spec-row"><span>Web · type</span><code>Inter 600 · 15px · #FFFFFF</code></div>
              <div className="bb-spec-row"><span>Web · hover</span><code>shine sweep + translateY(-1px)</code></div>
              <div className="bb-spec-row"><span>Web · disabled</span><code>opacity 0.45 · cursor not-allowed</code></div>
            </div>
          </section>

          {/*  ─────────────  08 · LAYOUT  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">08</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Layout</div>
                <h2 className="bb-h1">Editorial, in our own palette.</h2>
                <p className="bb-lede">
                  Pages read like a printed program, built from ZONA's own colors
                  and components. Rows instead of boxes, photos without frames,
                  names set large.
                </p>
              </div>
            </header>

            <div className="bb-voice">
              <div className="bb-voice__col bb-voice__col--do">
                <div className="bb-voice__label">Do</div>
                <div className="bb-rows-sample">
                  <div className="bb-rows-sample__row"><span>Favorite drink</span><b>Espresso martini</b></div>
                  <div className="bb-rows-sample__row"><span>When</span><b>Sat, Oct 18 · 11 PM</b></div>
                  <div className="bb-rows-sample__row"><span>Where</span><b>Brickell</b></div>
                </div>
                <ul className="bb-voice__list">
                  <li><span className="bb-voice__note">Plain rows with hairline dividers, rgba(255,255,255,0.1).</span></li>
                  <li><span className="bb-voice__note">Photos and flyers without frames, borders or outlines. Names large on a dark bottom fade over the photo.</span></li>
                  <li><span className="bb-voice__note">Section labels small, bold, sentence case. Actions are labeled buttons.</span></li>
                </ul>
              </div>

              <div className="bb-voice__col bb-voice__col--dont">
                <div className="bb-voice__label">Don't</div>
                <div className="bb-rows-sample bb-rows-sample--boxed">
                  <div className="bb-rows-sample__row"><span>Favorite drink</span><b>Espresso martini</b></div>
                  <div className="bb-rows-sample__row"><span>When</span><b>Sat, Oct 18 · 11 PM</b></div>
                  <div className="bb-rows-sample__row"><span>Where</span><b>Brickell</b></div>
                </div>
                <ul className="bb-voice__list">
                  <li><span className="bb-voice__note">Each fact in its own boxed card.</span></li>
                  <li><span className="bb-voice__note">Borders, outlines or glows around photos.</span></li>
                  <li><span className="bb-voice__note">Icon-only main actions.</span></li>
                </ul>
              </div>
            </div>
          </section>

          {/*  ─────────────  09 · FLYER TILES  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">09</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Flyer tiles</div>
                <h2 className="bb-h1">The flyer is the tile.</h2>
                <p className="bb-lede">
                  Borderless, edge to edge, with a soft bottom fade under the
                  title. The offering pills stay. Nothing else rides on top.
                </p>
              </div>
            </header>

            <div className="bb-voice">
              <div className="bb-voice__col bb-voice__col--do">
                <div className="bb-voice__label">Do</div>
                <div className="bb-flyer">
                  <div className="bb-flyer__art" />
                  <div className="bb-flyer__pills">
                    <span className="bb-flyer__pill">Table</span>
                    <span className="bb-flyer__pill bb-flyer__pill--guestlist">Guestlist</span>
                  </div>
                  <div className="bb-flyer__fade" />
                  <div className="bb-flyer__meta">
                    <div className="bb-flyer__title">Rooftop Saturday</div>
                    <div className="bb-flyer__sub">Brickell · 11 PM</div>
                  </div>
                </div>
                <ul className="bb-voice__list">
                  <li><span className="bb-voice__note">No outline. Soft bottom fade. Share, no heart.</span></li>
                  <li><span className="bb-voice__note">The offering pills stay, an approved exception: the offering word, like TABLE, TICKETS or DINNER, on translucent black, plus a purple GUESTLIST when the night has an open guest list.</span></li>
                </ul>
              </div>

              <div className="bb-voice__col bb-voice__col--dont">
                <div className="bb-voice__label">Don't</div>
                <div className="bb-flyer bb-flyer--outlined">
                  <div className="bb-flyer__art" />
                  <div className="bb-flyer__pills">
                    <span className="bb-flyer__chip">Hot</span>
                    <span className="bb-flyer__chip bb-flyer__chip--alt">New</span>
                  </div>
                  <span className="iconify bb-flyer__heart" data-icon="solar:heart-outline"></span>
                  <div className="bb-flyer__fade" />
                  <div className="bb-flyer__meta">
                    <div className="bb-flyer__title">Rooftop Saturday</div>
                    <div className="bb-flyer__sub">Brickell · 11 PM</div>
                  </div>
                </div>
                <ul className="bb-voice__list">
                  <li><span className="bb-voice__note">The blue to purple gradient outline. Don't add it to new tiles.</span></li>
                  <li><span className="bb-voice__note">A heart on event tiles. Any other pill badges or colored chips, anywhere.</span></li>
                </ul>
              </div>
            </div>
          </section>

          {/*  ─────────────  10 · PROFILES  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">10</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Profiles</div>
                <h2 className="bb-h1">A photo, a name, the facts.</h2>
                <p className="bb-lede">
                  Public and own profiles share one layout. A full-width square
                  photo, the name set large on its fade, one facts line, then
                  labeled rows. No cover photos.
                </p>
              </div>
            </header>

            <div className="bb-profile-stage">
              <div className="bb-phone bb-phone--profile">
                <div className="bb-profile__photo">
                  <div className="bb-profile__fade" />
                </div>
                <div className="bb-profile__identity">
                  <div className="bb-profile__name">Sofia</div>
                  <div className="bb-profile__facts">26 · Female · Member since 2024</div>
                </div>
                <div className="bb-profile__body">
                  <div className="bb-btn-row">
                    <button className="bb-btn bb-btn--lime bb-btn--medium bb-btn--square bb-btn--action">Send a drink</button>
                    <button className="bb-btn bb-btn--dark bb-btn--medium bb-btn--square bb-btn--action">Message</button>
                  </div>
                  <div className="bb-rows-sample">
                    <div className="bb-rows-sample__row"><span>Favorite drink</span><b>Espresso martini</b></div>
                    <div className="bb-rows-sample__row"><span>Tables</span><b>★ 4.8 · 6 tables · 0 no-shows</b></div>
                    <div className="bb-rows-sample__row"><span>Verification</span><b>Verified</b></div>
                  </div>
                  <div className="bb-gallery">
                    {[[176, 269], [269, 176]].map((col, c) => (
                      <div className="bb-gallery__col" key={c}>
                        {col.map((h, i) => (
                          <div key={i} className="bb-gallery__tile" style={{ height: h / 2 }} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Photo</span><code>full width · square · no frame · dark bottom fade</code></div>
              <div className="bb-spec-row"><span>Name</span><code>Inter 800 · 40 · letter-spacing -1.2 · on the fade</code></div>
              <div className="bb-spec-row"><span>Facts line</span><code>26 · Female · Member since 2024 · 15 · 600 · 70% white</code></div>
              <div className="bb-spec-row"><span>Rows</span><code>favorite drink · table record (public profile) · verification (own profile) · hairline dividers</code></div>
              <div className="bb-spec-row"><span>Table record</span><code>shown only when the server switch profileTableRecordEnabled is on</code></div>
              <div className="bb-spec-row"><span>Gallery</span><code>2 columns · staggered · heights 176 / 269, pattern short, tall, tall, short (shown at half size)</code></div>
              <div className="bb-spec-row"><span>Action row</span><code>lime main plus dark secondary · Send a drink + Message (public) · Edit profile + Add photos (own) · Medium, square at radius 12, an exception to the Medium pill (2026-10-17)</code></div>
              <div className="bb-spec-row"><span>Cover photo</span><code>none</code></div>
              <div className="bb-spec-row"><span>Header</span><code>no fixed tint over the photo · the header background fades in only as you scroll: blur on iOS, #050505 on Android · clear at the top (2026-10-16)</code></div>
            </div>
          </section>

          {/*  ─────────────  11 · LAST CALL  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">11</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Last call</div>
                <h2 className="bb-h1">An hour, running out.</h2>
                <p className="bb-lede">
                  The Last Call tile is the night's flyer with a clock on it. A
                  lime label counts down in the corner where the offering pills
                  sit, and a lime bar along the bottom shows the time left in the
                  hour. No red ring since 2026-10-16. Red stays for actions that
                  can't be undone.
                </p>
              </div>
            </header>

            <div className="bb-button-stage">
              <LastCallTile />
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Image</span><code>the night's flyer fills the tile · borderless flyer card · soft bottom fade</code></div>
              <div className="bb-spec-row"><span>Label</span><code>"Last call · mm:ss" · top left, where the TABLE and GUESTLIST pills sit · caption 11 / 600 · #C6FF3C · dark text shadow · no pill fill, no icon · ticks every second</code></div>
              <div className="bb-spec-row"><span>Bar</span><code>3pt · #C6FF3C · along the bottom edge, clipped to the tile's corners · full at the start of the 60-minute window, empty at the end</code></div>
              <div className="bb-spec-row"><span>Title</span><code>"&lt;table&gt; at &lt;event&gt;" · headline 18 / 700 · the table name alone when the night has no name</code></div>
              <div className="bb-spec-row"><span>Detail</span><code>Tonight · $3,000 minimum (the weekday, like Fri, for later nights) · secondary 13</code></div>
              <div className="bb-spec-row"><span>Border</span><code>none · no red ring, no outline, no glass sheen</code></div>
              <div className="bb-spec-row"><span>Row header</span><code>title plus a plain "See all" text link (see Screens)</code></div>
              <div className="bb-spec-row"><span>Component</span><code>Zona/src/components/LastCallShort.tsx</code></div>
            </div>
          </section>

          {/*  ─────────────  12 · NAVIGATION  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">12</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Navigation</div>
                <h2 className="bb-h1">Quiet, on purpose.</h2>
                <p className="bb-lede">
                  Navigation is supposed to disappear into the page. Tabs use a
                  thin underline. Sidebar items use a faint tint. Inline links
                  are just colored text. None of these should ever look like the
                  primary CTA.
                </p>
              </div>
            </header>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Tabs · container</span><code>border-bottom 1px rgba(255,255,255,0.06)</code></div>
              <div className="bb-spec-row"><span>Tabs · inactive</span><code>color rgba(255,255,255,0.5) · 13px · 600</code></div>
              <div className="bb-spec-row"><span>Tabs · hover</span><code>color rgba(255,255,255,0.85)</code></div>
              <div className="bb-spec-row"><span>Tabs · active</span><code>white text + 2px #4F30EA underline</code></div>
              <div className="bb-spec-row"><span>Sidebar · inactive</span><code>color rgba(255,255,255,0.5) · icon opacity 0.5</code></div>
              <div className="bb-spec-row"><span>Sidebar · hover</span><code>bg rgba(255,255,255,0.06) · color #fff · icon opacity 0.8</code></div>
              <div className="bb-spec-row"><span>Sidebar · active</span><code>bg rgba(79,48,234,0.1) · color #fff · weight 600</code></div>
              <div className="bb-spec-row"><span>Text link · rest</span><code>color #4F30EA · no border · no bg</code></div>
              <div className="bb-spec-row"><span>Text link · hover</span><code>color #7229F5 · underline (3px offset)</code></div>
              <div className="bb-spec-row"><span>Filter chip · active</span><code>bg rgba(79,48,234,0.1) · faint, not solid</code></div>
            </div>
          </section>

          {/*  ─────────────  13 · FORMS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">13</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Forms</div>
                <h2 className="bb-h1">Frosted glass.</h2>
                <p className="bb-lede">
                  Form wrappers carry blur. Inputs are 12-pixel radius with a
                  hero-purple focus ring at low opacity.
                </p>
              </div>
            </header>

            <div className="bb-input-stage">
              <input type="text" placeholder="email@getzona.app" />
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Wrapper bg</span><code>rgba(255,255,255,0.04) + blur(24px) saturate(160%)</code></div>
              <div className="bb-spec-row"><span>Wrapper border</span><code>1px rgba(255,255,255,0.08)</code></div>
              <div className="bb-spec-row"><span>Wrapper radius</span><code>20px</code></div>
              <div className="bb-spec-row"><span>Input rest</span><code>rgba(255,255,255,0.06) bg + 1px border</code></div>
              <div className="bb-spec-row"><span>Input focus</span><code>rgba(255,255,255,0.07) bg + 1px rgba(114,41,245,0.55)</code></div>
              <div className="bb-spec-row"><span>Focus ring</span><code>0 0 0 3px rgba(114,41,245,0.18)</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Glass on buttons.</h3>
              <p className="bb-prose">
                In the app, every filled button and many surfaces carry the
                GlassSheen: a white gloss on the top half, a darker rim along the
                bottom and a 1pt edge. Lime stays exactly as it is live in
                2.0.50. Purple looked too white because its rim was a pale
                blue-white at 34% with an 8% gloss, which washed the lower half
                of the button out. Purple and red now have half the gloss and a
                dark rim of their own hue, the way lime's rim is a dark green.
                Dark buttons, secondary buttons and panels sit at half, so they
                stay quieter than lime. Log in and custom fills are unchanged.
                Approved 2026-10-17, replacing the flat 25% of Zona #532.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-glass-grid">
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--purple-old bb-btn--small bb-btn--list">See tables</button>
                  <div className="bb-mono">Purple before</div>
                </div>
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">See tables</button>
                  <div className="bb-mono">Purple now</div>
                </div>
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--red-old bb-btn--medium">Remove</button>
                  <div className="bb-mono">Red before</div>
                </div>
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--red bb-btn--medium">Remove</button>
                  <div className="bb-mono">Red now</div>
                </div>
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--lime bb-btn--medium">Join</button>
                  <div className="bb-mono">Lime, as in 2.0.50</div>
                </div>
                <div className="bb-glass-grid__cell">
                  <button className="bb-btn bb-btn--dark bb-btn--medium">Message</button>
                  <div className="bb-mono">Dark, at half</div>
                </div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Lime</span><code>gloss 13% to 3% white · rim rgba(70,110,0,0.24) · edge 10% black, top 20% white · as live in 2.0.50</code></div>
              <div className="bb-spec-row"><span>Purple</span><code>gloss 4% to 1% white · rim rgba(30,15,120,0.24) · edge 10% black, top 10% white</code></div>
              <div className="bb-spec-row"><span>Red</span><code>gloss 3.5% to 0.8% white · rim rgba(110,0,0,0.22) · edge 10% black, top 10% white</code></div>
              <div className="bb-spec-row"><span>Was</span><code>purple gloss 8% · rim rgba(165,185,255,0.34) · red gloss 7% · rim rgba(255,165,165,0.28) · both pale rims read as white</code></div>
              <div className="bb-spec-row"><span>Dark and secondary</span><code>gloss 4% to 1% white · rim rgba(150,160,255,0.07) · edge 4%, top 8% white · chips, fields, Message, Add photos, Decline · half of before</code></div>
              <div className="bb-spec-row"><span>Panel</span><code>gloss 4.5% to 1% white · rim rgba(165,185,255,0.08) · edge 4%, top 9% white · banners, popups, tickets, sheets · half of before</code></div>
              <div className="bb-spec-row"><span>Unchanged</span><code>Log in and disabled buttons (gloss 4%, rim 6%) · custom fills like a host's theme color (gloss 7%, black rim 16%)</code></div>
              <div className="bb-spec-row"><span>Bands</span><code>gloss on the top 52%, at most 30pt (38% on panels) · rim on the bottom 42%, at most 26pt · surfaces under 26pt get a hairline edge and a lighter gloss</code></div>
            </div>
          </section>

          {/*  ─────────────  14 · HALOS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">14</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Halos</div>
                <h2 className="bb-h1">A unified backdrop.</h2>
                <p className="bb-lede">
                  Every landing uses the same two-layer halo system. Static, never
                  animated. Identical across surfaces.
                </p>
              </div>
            </header>

            <div className="bb-halo-stage">
              <div className="bb-halo-stage__inner" />
            </div>

            <div className="bb-grid-2">
              <div>
                <div className="bb-eyebrow">Layer 1 · Page wash</div>
                <p className="bb-prose">
                  Two large radial gradients on the page background. Blue at 10% / -10%
                  offset. Purple at 90% / 110%. Both at 0.10 to 0.12 opacity. Never animated.
                </p>
              </div>
              <div>
                <div className="bb-eyebrow">Layer 2 · Hero halo</div>
                <p className="bb-prose">
                  Pseudo-element with three blurred radials: purple and two blues.
                  Applied at 0.14 to 0.28 opacity with a 40-pixel blur. Sits behind the
                  hero content.
                </p>
              </div>
            </div>
          </section>

          {/*  ─────────────  15 · SYSTEM  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">15</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">System</div>
                <h2 className="bb-h1">Where the values live.</h2>
                <p className="bb-lede">
                  One canonical source. Three mirrored copies. One mobile palette
                  subset. Edits flow from the canonical only.
                </p>
              </div>
            </header>

            <div className="bb-system-stack">
              <div className="bb-system-row"><span className="bb-system-row__role">Canonical</span><code>Zona-ads-portal/src/styles/tokens.ts</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">Mirror · Business</span><code>Business-panel-new/src/styles/tokens.ts</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">Mirror · Affiliates</span><code>zona-affiliates-portal/src/styles/tokens.ts</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">Mirror · Admin</span><code>Admin-panel-new/src/styles/tokens.ts</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">SCSS port</span><code>Admin-panel-new/src/styles/partials/_variables.scss</code></div>
              <div className="bb-system-row"><span className="bb-system-row__role">Mobile</span><code>Zona/constants/brandTokens.js</code></div>
            </div>
          </section>

          {/*  ─────────────  16 · RADAR  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">16</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Radar</div>
                <h2 className="bb-h1">The heat-map language.</h2>
                <p className="bb-lede">
                  The live radar speaks in pins. Color is the state, the glyph
                  is the offer. Nine glyphs in two families. Venue pins: one per
                  venue, whichever offer ranks highest. Member plan pins: a plan
                  someone is hosting, always lime, the glyph set by who hosts it.
                </p>
              </div>
            </header>

            <div className="bb-glyph-grid">
              {radarGlyphs.map((g) => (
                <div className="bb-glyph-cell" key={g.name}>
                  <div className="bb-glyph-dot" style={{ background: g.dot }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill={g.dot === "#C6FF3C" ? "#0a0a0f" : "#ffffff"} d={g.path} />
                    </svg>
                  </div>
                  <div className="bb-glyph-cell__meta">
                    <div className="bb-glyph-cell__name">{g.label}</div>
                    <div className="bb-glyph-cell__use">{g.use}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Live · lime</span><code>#C6FF3C · the default live signal · every venue pin that isn't amber or red, and every member plan pin</code></div>
              <div className="bb-spec-row"><span>Dinner · amber</span><code>#E67E22 · a venue with dinner and no bottle service</code></div>
              <div className="bb-spec-row"><span>Last call · red</span><code>#E11D48 · closing soon, pin pulses hot</code></div>
              <div className="bb-spec-row"><span>Glyph fill</span><code>#0a0a0f on lime · #FFFFFF on red / amber</code></div>
              <div className="bb-spec-row"><span>Pin</span><code>15px dot · triple pulse ring · one pin per venue, and one per member plan</code></div>
              <div className="bb-spec-row"><span>Venue pin order</span><code>hourglass (last call) &gt; bottle &gt; restaurant &gt; ticket &gt; guest list (people, or ticket when it sells tiers) &gt; lock (no open offer)</code></div>
              <div className="bb-spec-row"><span>Member plan pin</span><code>headphones (Verified DJ) &gt; star (Verified Promoter) &gt; plan (anyone else)</code></div>
              <div className="bb-spec-row"><span>Pin colors</span><code>set by the pin component, not a free choice</code></div>
              <div className="bb-spec-row"><span>Blue</span><code>#0A3FD6 exists in the pin component and is not used by the radar today</code></div>
              <div className="bb-spec-row"><span>Two reds</span><code>radar #E11D48 and the can't-be-undone #FF3B30 are different reds in different components. Neither is a general accent. The Last Call tile on Home is lime, not red</code></div>
              <div className="bb-spec-row"><span>Read from the app</span><code>2026-09-17</code></div>
            </div>
          </section>

          {/*  ─────────────  17 · STATE SCREENS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">17</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">State screens</div>
                <h2 className="bb-h1">One block for when the page can't show.</h2>
                <p className="bb-lede">
                  Offline, not found, no access and the rest share one block:
                  the map pin, a large title, one muted line and one lime button
                  that does something useful. It sits at the bottom of the
                  screen, where the thumb is.
                </p>
              </div>
            </header>

            <div className="bb-profile-stage">
              <div className="bb-phone bb-state">
                <div className="bb-state__block">
                  <DropPin size={36} />
                  <div className="bb-state__title">You're offline</div>
                  <div className="bb-state__line">Check your connection and try again.</div>
                  <button className="bb-btn bb-btn--lime bb-state__button">Try again</button>
                  <div className="bb-state__status">Still offline</div>
                </div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Pin</span><code>the map pin, assets/icons/drop.png · 36 · 20 below it</code></div>
              <div className="bb-spec-row"><span>Title</span><code>display 40 / 800 · white</code></div>
              <div className="bb-spec-row"><span>Line</span><code>one line · body 15 · 60% white</code></div>
              <div className="bb-spec-row"><span>Button</span><code>Primary large · full width · 32 above it · always a useful next step</code></div>
              <div className="bb-spec-row"><span>Status</span><code>optional · secondary 13 · 60% white · centered under the button (Checking…, Still offline)</code></div>
              <div className="bb-spec-row"><span>Position</span><code>left aligned · 16 from each side · anchored to the bottom, 40 up (more on phones with a home indicator), or above the tab bar where it shows</code></div>
              <div className="bb-spec-row"><span>Offline</span><code>You're offline · Check your connection and try again. · Try again, which really re-checks the connection</code></div>
              <div className="bb-spec-row"><span>Not found</span><code>each page sends you somewhere useful: Plan not found → See plans · This drink isn't available → Go to Inbox · We couldn't find this request → Go to Home</code></div>
              <div className="bb-spec-row"><span>Also used by</span><code>verify phone · login link states · screenshot blocked · no access</code></div>
              <div className="bb-spec-row"><span>Force update</span><code>kept as it is, her call on 2026-10-16. It does not use this block</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Couldn't load, inside a page.</h3>
              <p className="bb-prose">
                When one part of a page fails, like the map, the inbox or Meet
                People, a compact block sits centered in that space so the header
                and tab bar keep working.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-loaderror">
                <span className="iconify bb-loaderror__icon" data-icon="feather:alert-circle"></span>
                <div className="bb-loaderror__title">Couldn't load messages</div>
                <div className="bb-loaderror__line">Check your connection and try again.</div>
                <button className="bb-btn bb-btn--lime bb-btn--medium">Try again</button>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Icon</span><code>alert-circle · 24 · 46% white</code></div>
              <div className="bb-spec-row"><span>Title</span><code>headline 18 / 700 · centered · Couldn't load people, Couldn't load messages</code></div>
              <div className="bb-spec-row"><span>Line</span><code>body 15 · 60% white · Check your connection and try again.</code></div>
              <div className="bb-spec-row"><span>Button</span><code>Primary medium · Try again · content width, min 120</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Update banner.</h3>
            </div>

            <div className="bb-button-stage">
              <div className="bb-update-banner">
                <DropPin size={32} />
                <div className="bb-update-banner__text">
                  <div className="bb-update-banner__title">Update available</div>
                  <div className="bb-update-banner__sub">New features and fixes</div>
                </div>
                <button className="bb-btn bb-btn--lime bb-btn--small">Update</button>
                <span className="iconify bb-update-banner__x" data-icon="feather:x"></span>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Banner</span><code>#111111 · hairline under it · pinned under the status bar</code></div>
              <div className="bb-spec-row"><span>Content</span><code>map pin 32 · Update available in body strong · one secondary line · Primary small "Update" · close x</code></div>
            </div>
          </section>

          {/*  ─────────────  18 · SCREENS  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">18</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Screens</div>
                <h2 className="bb-h1">Decisions, screen by screen.</h2>
                <p className="bb-lede">
                  Calls made on single screens that the rules above don't cover
                  on their own. Each one is how the screen works now.
                </p>
              </div>
            </header>

            <div className="bb-subhead">
              <h3 className="bb-h2">Home row headers.</h3>
              <p className="bb-prose">
                A row's title on the left and a plain See all text link on the
                right, in place of the arrow.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-row-head">
                <span className="bb-row-head__title">Miami Drops</span>
                <span className="bb-row-head__link">See all</span>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Title</span><code>headline 18 / 700 · white</code></div>
              <div className="bb-spec-row"><span>See all</span><code>Text button · body strong 15 / 600 · white · no arrow</code></div>
              <div className="bb-spec-row"><span>Where</span><code>Last Call row and the venue rows on Home (2026-10-16)</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Partner row on the event page.</h3>
              <p className="bb-prose">
                Nights at LIV and Gekko book on the venue's own site. Their event
                page shows one offer row: ON PARTNER SITE on the left, in
                capitals by her call (an exception to sentence case), and a
                small lime Details pill at 108 with the external link icon on the
                right. The venue's partner note is not shown. Every other venue
                keeps its See tables row.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-offer-row">
                <span className="bb-offer-row__title">ON PARTNER SITE</span>
                <button className="bb-btn bb-btn--lime bb-btn--small bb-btn--list bb-offer-row__btn">
                  <span className="iconify" data-icon="feather:external-link"></span>
                  Details
                </button>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Left</span><code>ON PARTNER SITE · headline 18 / 700 · white · no Bottle Service title, no note under it</code></div>
              <div className="bb-spec-row"><span>Right</span><code>Primary small, width 108 · Details · external-link icon 14, gap 6 · opens the partner site</code></div>
              <div className="bb-spec-row"><span>When</span><code>the night has a partner payment link · the venue's hide switch still hides it</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-16 · Small at 96 on 2026-10-17, 108 from 2.0.52</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Offer rows on the event page.</h3>
              <p className="bb-prose">
                Every offer row's button is a Small pill at 108, so the page
                doesn't look bulky and the right edges line up down the page.
                Purple for See tables and tickets, lime for the partner Details.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-people">
                <div className="bb-offer-row">
                  <div>
                    <div className="bb-offer-row__title">Bottle Service</div>
                    <div className="bb-offer-row__sub">From $325</div>
                  </div>
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">See tables</button>
                </div>
                <div className="bb-offer-row">
                  <div>
                    <div className="bb-offer-row__title">Tickets</div>
                    <div className="bb-offer-row__sub">$150</div>
                  </div>
                  <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">Get ticket</button>
                </div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Buttons</span><code>Details (external-link icon 14) · See tables · Get ticket · View ticket · Vouchers · Redeem · See tables on a dinner row</code></div>
              <div className="bb-spec-row"><span>Size</span><code>Small · pill, radius 19 · width 108 · label 15 / 600</code></div>
              <div className="bb-spec-row"><span>Was</span><code>Medium, 44 tall, hugging the label</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-17</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Host guest list.</h3>
              <p className="bb-prose">
                Long names were running into the buttons. A join request now
                shows the name, a muted "wants to join" line, then Accept in
                purple and Decline in dark, both at 108, under the name. Decline
                is dark, not red: it can be undone. Each guest has a quiet ⋯
                that opens a sheet with one red action, Remove from guest list.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-people">
                <div className="bb-people__section">
                  <div className="bb-people__label">Join requests</div>
                  <div className="bb-people__request">
                    <div className="bb-people__row" style={{ padding: 0 }}>
                      <span className="bb-people__avatar" />
                      <div className="bb-people__text">
                        <div className="bb-people__name">Alexander Whitfield-Montgomery</div>
                        <div className="bb-people__sub">wants to join</div>
                      </div>
                    </div>
                    <div className="bb-people__actions">
                      <button className="bb-btn bb-btn--purple bb-btn--small bb-btn--list">Accept</button>
                      <button className="bb-btn bb-btn--dark bb-btn--small bb-btn--list">Decline</button>
                    </div>
                  </div>
                </div>
                <div className="bb-people__section">
                  <div className="bb-people__label">Guests</div>
                  <div className="bb-people__row">
                    <span className="bb-people__avatar" />
                    <span className="bb-people__text bb-people__name">Sam Okafor</span>
                    <span className="bb-people__more" aria-hidden="true">⋯</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bb-button-stage">
              <div className="bb-sheet">
                <div className="bb-sheet__grab" />
                <div className="bb-sheet__head"><span className="bb-people__avatar" />Sam Okafor</div>
                <div className="bb-sheet__row">Remove from guest list</div>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Request</span><code>avatar 48 · name in body strong, one line · "wants to join" secondary 13, 60% white</code></div>
              <div className="bb-spec-row"><span>Actions</span><code>Accept Purple small and Decline Secondary small · width 108 · gap 10 · 10 below the name · indented 60 to line up with the name</code></div>
              <div className="bb-spec-row"><span>Guest ⋯</span><code>host only · 36 round · #1C1C1E · 1px 10% white border · more-horizontal icon 18</code></div>
              <div className="bb-spec-row"><span>Sheet</span><code>the guest's photo and name in headline 18 · Remove from guest list in #FF3B30 body strong · then the Remove confirm popup</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-17, option B</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Rate your table.</h3>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Done</span><code>Primary large, lime, full width · was a white button · the screen's one main action</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-17</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Ticket link on a plan.</h3>
              <p className="bb-prose">
                When a host adds a ticket link, it shows as a Get tickets row
                inside the plan's details card, not as a button. I'm in stays the
                plan's only main button.
              </p>
            </div>

            <div className="bb-button-stage">
              <div className="bb-plan-row">
                <span className="iconify" data-icon="ion:ticket"></span>
                <span className="bb-plan-row__link">Get tickets</span>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Row</span><code>ticket icon 16 · Get tickets · secondary 13 · underlined · in the plan's theme color</code></div>
              <div className="bb-spec-row"><span>When</span><code>signed in, and the plan has a ticket link</code></div>
              <div className="bb-spec-row"><span>Main button</span><code>I'm in, alone</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-16</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Meet People grid.</h3>
              <p className="bb-prose">
                Six people, 2 across and 3 down, all on screen with no scrolling.
                Tiles start square and grow taller, up to 4:5, to fill the space
                down to Shuffle, so the gap above it stays small (about 14pt) on
                every iPhone. Shorter screens stay square.
              </p>
            </div>

            <div className="bb-profile-stage">
              <div className="bb-meet-grid">
                {Array.from({ length: 6 }, (_, i) => (
                  <div className="bb-meet-grid__tile" key={i} />
                ))}
                <button className="bb-btn bb-btn--lime bb-meet-grid__shuffle">Shuffle</button>
              </div>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Grid</span><code>2 × 3 · gap 10 · 16 from each side · radius 4</code></div>
              <div className="bb-spec-row"><span>Tile</span><code>width from the screen · height up to 1.25 × width (4:5), capped by the space down to Shuffle</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-16</code></div>
            </div>

            <div className="bb-subhead">
              <h3 className="bb-h2">Plan designer day choices.</h3>
            </div>

            <div className="bb-spec-rows">
              <div className="bb-spec-row"><span>Day choices</span><code>Tonight · Tomorrow · a date · headline 18 / 700</code></div>
              <div className="bb-spec-row"><span>Start times</span><code>title 22, unchanged</code></div>
              <div className="bb-spec-row"><span>Approved</span><code>2026-10-17</code></div>
            </div>
          </section>

          {/*  ─────────────  19 · ANDROID  ─────────────  */}
          <section className="bb-spread">
            <header className="bb-section-head">
              <div className="bb-section-head__num">19</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Android</div>
                <h2 className="bb-h1">Where Android draws it differently.</h2>
                <p className="bb-lede">
                  Three rules learned from Android bugs. A screen that looks
                  right on iPhone can still break on Android, so check both.
                </p>
              </div>
            </header>

            <div className="bb-voice__rules">
              <div className="bb-rule">
                <div className="bb-rule__num">01</div>
                <p>Don't use adjustsFontSizeToFit on text with no set width. Android shrinks it straight to minimumFontScale: the designer's day labels showed at 9pt.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">02</div>
                <p>The app uses adjustPan on Android, which only scrolls the focused field into view. A button under a text field hides behind the keyboard. Put the action above the field while typing, like the Wink note, or keep actions out of the keyboard's area.</p>
              </div>
              <div className="bb-rule">
                <div className="bb-rule__num">03</div>
                <p>Bottom bars and footers add the Android navigation bar inset (useBottomInset or useTabBarSpace), or they sit behind the tab bar. The closed chat's Contact support button was cut off.</p>
              </div>
            </div>
          </section>

          {/*  ─────────────  20 · CHANGE LOG  ─────────────  */}
          <section className="bb-spread bb-print-hide">
            <header className="bb-section-head">
              <div className="bb-section-head__num">20</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">Change log</div>
                <h2 className="bb-h1">What changed, and when.</h2>
              </div>
            </header>

            <div className="bb-dev">
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-10-17</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Small pills a little bigger for 2.0.52: 38 tall, radius 19, label 15, 108 in lists (was 34, 17, 13, 96). Labels shortened to fit: Vouchers (en), Canjear (es), Utiliser (fr) (Zona #542).</li>
                  <li>Verified against Zona main at a7abcd2b. The button decisions below were verified against Zona main at 925b107f.</li>
                  <li>Buttons: Large stays square at 12. Medium is a pill at 22 and Small a pill at 17. Profile actions (Send a drink, Message, Message {'{name}'}, Edit profile, Add photos) stay square at 12 through the Button's <code>square</code> prop. This replaces the review note on the squarer corners from Zona #498 (Zona #533).</li>
                  <li>Buttons: popups (alerts and the ask popup) and the profile action styles stay square at 12, now that Medium is a pill. Popup colors are unchanged (Zona #534, main 925b107f).</li>
                  <li>Buttons: Small pills in a list or pair share one width, 96, and hug their label when alone. Labels were shortened to fit: Join the table to Join, Redeem voucher to Redeem (Zona #533).</li>
                  <li>Buttons: five kinds of color and no grey. Lime for the one main action, purple for other actions and purchases, dark for the alternative, text only for backing out, red only for what can't be undone. Popups keep their own buttons, colors and square corners. Look, Clear all, See venues near you and the signed-out Sign in turned dark in Zona #535.</li>
                  <li>Buttons: the blue to purple gradient is retired on buttons. About 75 buttons that used it are solid purple, and the signup steps (name, phone, verify phone, email, verify email, personal info, password, vibe, add photo) are lime. Popups and the VIP gradient are unchanged (Zona #535).</li>
                  <li>Headers: one rule on every screen. A 44pt bar right under the status bar, the back arrow 24pt and 16pt from the left, centered on the bar. About 70 screens moved to it. Signup and log in keep their own layout, her call (Zona #536).</li>
                  <li>Screens: the signup title matches Log in, a 44pt Drop with 20pt to the title (Zona #537).</li>
                  <li>Glass: lime stays as live in 2.0.50. Purple and red get half the gloss and a dark rim of their own hue instead of a pale one, so they no longer wash out white. Dark, secondary and panel surfaces at half. Log in and custom fills unchanged. Replaces the flat 25% from Zona #532 (Zona #533).</li>
                  <li>Screens: event page offer rows are Small pills at 96, the partner Details pill included (icon 14). Host guest list uses option B: "wants to join" with Accept and Decline under the name, and a ⋯ sheet with Remove from guest list. Rate your table's Done is lime (Zona #533).</li>
                  <li>Screens: plan designer day choices (Tonight, Tomorrow, a date) moved up to headline 18. Start times stay at title 22 (Zona #530).</li>
                  <li>Android: three rules added, on adjustsFontSizeToFit, adjustPan and the navigation bar inset (Zona #529).</li>
                </ul>
              </div>
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-10-16</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Last Call: the red countdown ring is gone. A lime "Last call · mm:ss" label sits where the offering pills go, a 3pt lime bar shows the time left, and the title reads "&lt;table&gt; at &lt;event&gt;" (Zona #515). Countdown Red now marks only actions that can't be undone.</li>
                  <li>Screens: Home row headers use a plain See all text link instead of the arrow (Zona #515).</li>
                  <li>Screens: the LIV and Gekko event page shows one partner row, ON PARTNER SITE in capitals (an approved exception) and a medium lime Details button with the external link icon. The venue's partner note is not shown (Zona #514, #517, #518).</li>
                  <li>Screens: a plan's ticket link is a Get tickets row in its details card. I'm in stays the only main button (Zona #516).</li>
                  <li>Profiles: no fixed tint over the photo. The header background fades in only as you scroll (Zona #519, #526).</li>
                  <li>Screens: Meet People tiles grow up to 4:5 to fill the space down to Shuffle (Zona #527).</li>
                  <li>State screens: one shared block for offline, not found, verify phone, login link, screenshot blocked and no access, plus the inline Couldn't load block and the update banner. The force update screen is unchanged (Zona #522, #523).</li>
                </ul>
              </div>
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-10-15</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>The seven-step type scale.</li>
                  <li>Buttons named as a role plus a size: Primary, Gradient, Purple, Secondary, Text button, Chip and Toggle.</li>
                  <li>Which color goes where: lime is the night, gradient is getting something done, purple is opening or going deeper, red is can't be undone (Refund and Refunded).</li>
                  <li>This page became ZONA's one brand book. The zona-brain copy now points here.</li>
                </ul>
              </div>
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-10-14</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Approved 2026-10-13 and 14, verified against Zona main at 65665e3e.</li>
                  <li>Button sizes approved: Large 50, Medium 44, Small 34, Chip 34, Toggle 40 and Text button 44, after an audit of 301 buttons on reachable screens.</li>
                  <li>Lime #C6FF3C became the primary action color in the app, one per screen with a #050505 label. The old "never the primary CTA" rule is gone.</li>
                  <li>Secondary buttons became dark solid #1C1C1E with a white label, the same shape as the lime button.</li>
                  <li>The blue to purple gradient stopped being the default look for main app actions. On 2026-10-15 it became the Gradient role.</li>
                  <li>Typography: Inter bundled natively in weights 300 to 900. Plan and invite titles use eight fixed catalog faces plus Inter Black.</li>
                  <li>Labels are sentence case. No small, letter-spaced all-caps labels.</li>
                  <li>Editorial layout language added: rows and hairlines instead of boxed cards, frameless photos, names on a dark fade.</li>
                  <li>Flyer tiles, profiles and the Last Call tile specified.</li>
                  <li>Red #FF3B30 added as a functional color for the Last Call countdown.</li>
                  <li>Copy rules consolidated: no exclamation marks, no em dashes or "--", no "Hi there", never "dashboard".</li>
                </ul>
              </div>
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-09-17</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Radar glyphs re-read from the app and documented, including the promoter star that shipped on 2026-09-09.</li>
                </ul>
              </div>
              <div className="bb-dev__group">
                <h3 className="bb-name">2026-07-04</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Gradient Magenta #B73AFF retired everywhere. The gradient went from three stops to two.</li>
                  <li>The gradientShimmerBrand token was changed to a purple and blue sweep: no magenta, no hot pink.</li>
                </ul>
              </div>
            </div>
          </section>

          {/*  ─────────────  21 · FOR DEVELOPERS  ─────────────  */}
          <section className="bb-spread bb-print-hide">
            <header className="bb-section-head">
              <div className="bb-section-head__num">21</div>
              <div className="bb-section-head__meta">
                <div className="bb-eyebrow">For developers</div>
                <h2 className="bb-h1">Where it lives in code.</h2>
                <p className="bb-lede">
                  Files, components and props behind the rules above. Paths are in
                  the Zona app repo unless they say otherwise.
                </p>
              </div>
            </header>

            <div className="bb-dev">
              <div className="bb-dev__group">
                <h3 className="bb-name">This brand book</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>The page: Admin-panel-new <code>src/pages/BrandBook/BrandBook.jsx</code> and <code>BrandBook.scss</code>. Change the brand here, then update <code>LAST_UPDATED</code> at the top of the file.</li>
                  <li><code>zona-brain/brand-book.md</code> is only a pointer to this page.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Buttons</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Shared component: <code>src/components/Button/index.tsx</code>. Tokens: <code>constants/brandTokens.js</code> (<code>LIME</code>, <code>PAGE_BG</code>).</li>
                  <li>Sizes and shapes live in <code>BUTTON_SIZES</code> in the Button file: large radius 12, medium 22, small 19 (38 tall, 15pt), small <code>listWidth</code> 108. <code>BUTTON_PAIR_GAP</code> is 10.</li>
                  <li><code>square</code>: corners at the large radius instead of a pill. Used only on the profile actions in <code>app/(home)/userProfile/[profileId].tsx</code> and <code>app/(home)/profile/index.jsx</code>.</li>
                  <li><code>inList</code>: the small size's fixed 108 width, label on one line with a 4pt inset. Used on the guest list, <code>src/components/TableInvites/TableInvitesInbox.jsx</code> (Join, Approve), <code>src/components/TableInvites/JoinableTables.jsx</code>, <code>src/components/Chat/InboxRequests.tsx</code>, <code>app/(home)/notifications/index.jsx</code>, <code>app/(home)/profile/blocked.tsx</code> and the event page offer rows in <code>app/(home)/events/[eventId].jsx</code>.</li>
                  <li>Popups: <code>src/contexts/alertContext.tsx</code> and <code>src/components/AskPopup.tsx</code>. Their buttons keep their own fills and take <code>BUTTON_SIZES.large.radius</code> (12), square on purpose (Zona #534).</li>
                  <li>Profile action styles in <code>src/components/profile/profileStyles.ts</code> also take <code>BUTTON_SIZES.large.radius</code>, matching the <code>square</code> prop (Zona #534).</li>
                  <li>Role and size props: <code>{'<Button type="primary" size="medium">'}</code>. Every button is a role (Primary, Purple, Secondary, Text button, Chip, Toggle) plus a size (Large, Medium, Small).</li>
                  <li>Lime: <code>useSolidColor solidColor={'{LIME}'}</code> (or <code>"#C6FF3C"</code>). The Button sets the label and loading spinner to #050505 on lime by itself; icons passed in should also be #050505.</li>
                  <li>No type: a Button with no type or fill is solid #4F30EA with the <code>brand</code> glass, the same as <code>type="purple"</code>. There is no gradient type anymore (Zona #535). Never hand-roll a gradient on a button.</li>
                  <li>Purple: <code>type="purple"</code>, solid #4F30EA, white label.</li>
                  <li>Secondary: <code>type="secondary"</code> (Message, Add photos, Decline), #1C1C1E with the <code>secondaryButton</code> glass. A plain <code>solidColor="#1C1C1E"</code> (Log in) keeps the <code>darkButton</code> glass.</li>
                  <li>Lime ships on: Send a drink (<code>app/(home)/userProfile/[profileId].tsx</code>), Join the guestlist (<code>app/(home)/events/[eventId].jsx</code>), Shuffle (<code>app/(home)/discover/index.jsx</code>), Request a table and Book on partner site (<code>app/(home)/events/bottleService/[eventId].jsx</code>), Sign up (<code>app/(auth)/chooseAuth.jsx</code>), Edit profile (<code>app/(home)/profile/index.jsx</code>), Done on Rate your table (<code>app/(home)/rateNight.jsx</code>, <code>type="primary"</code>).</li>
                  <li>Secondary ships on: Log in under Sign up (<code>app/(auth)/chooseAuth.jsx</code>), Message next to Send a drink (<code>app/(home)/userProfile/[profileId].tsx</code>), Add photos (<code>app/(home)/profile/index.jsx</code>), Decline on the guest list (<code>app/(home)/userGuestList.jsx</code>).</li>
                  <li>Glass: <code>TONES</code> in <code>src/components/GlassSheen/index.tsx</code>. The Button picks the tone from the fill: <code>brand</code> for purple, <code>lime</code>, <code>danger</code> for #FF3B30, <code>secondaryButton</code> for <code>type="secondary"</code>, <code>darkButton</code> for a plain #1C1C1E.</li>
                  <li>Shortened labels: <code>tableInvites.accept</code> (Join) and <code>eventPage.redeemVoucher</code> (Redeem) in <code>src/i18n/locales/</code>.</li>
                  <li>Lime also ships on the signup steps, <code>type="primary"</code>: Continue, Verify phone, Verify email, Finish on Add photo, in <code>app/(auth)/(signup)/</code>.</li>
                  <li>Purple (no type) ships on: Log in Continue, Save, Submit, Pay, Apply, Update, Get verified, and every organizer tool.</li>
                  <li>Headers: <code>src/constants/header.ts</code>. <code>HEADER_TOP</code> (status bar height), <code>HEADER_HEIGHT</code> 44, <code>HEADER_SIDE</code> 16, <code>BACK_ICON_SIZE</code> 24; <code>headerBar</code> for in-flow headers, <code>headerOverlay</code> over hero images. New screens use these, never their own top padding (Zona #536).</li>
                  <li>Button labels are sentence case in every language file since Zona #498.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Colors</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>The red destructive text on Delete group, Leave group and Remove from guest list is in <code>app/(home)/userGuestList.jsx</code>.</li>
                  <li>The red Refund and Refunded button is on the bottle service page.</li>
                  <li>Token files are listed under System.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Fonts</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Inter v4.1 files: <code>assets/fonts/Inter-*.ttf</code>, weights 300 to 900 plus italics 300 to 600.</li>
                  <li>Registered by the config plugin <code>plugins/withInterFont.js</code>: iOS UIAppFonts, Android <code>res/font/inter.xml</code>. Inter-Black is also its own family so older Android still draws Black.</li>
                  <li>App-wide default in <code>src/setup/defaultFont.ts</code>: every Text and TextInput, plus navigation headers and tab labels.</li>
                  <li><code>src/services/planFonts.js</code> loads plan and invite title fonts on demand and refuses catalog fonts named Inter, because another face named Inter joins the system Inter family and can take over every Inter request.</li>
                  <li>Title font catalog: <code>src/scripts/planThemes/catalogFonts.js</code> in the server repo (zona-superadmin-server).</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Labels and layout</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>No <code>textTransform: 'uppercase'</code> on UI labels. Sentence case was applied app-wide in Zona PR #483.</li>
                  <li>Hairline color: <code>HAIRLINE</code> in <code>src/components/profile/profileStyles.ts</code>, <code>rgba(255,255,255,0.1)</code>.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Flyer tiles</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Shared card: <code>src/components/FlyerCard/index.jsx</code>. Pass <code>bordered={'{false}'}</code>; <code>bordered</code> is the component default and draws the 1px gradient outline.</li>
                  <li>Venue and event flyer rows already pass <code>bordered={'{false}'}</code>. The outline still draws on the wallet tickets row (<code>src/components/TicketsShort.tsx</code>), your guest lists (<code>src/components/UserGuestListShort.jsx</code>) and most See all variants in <code>app/(home)/content.jsx</code>. Don't add it to new tiles.</li>
                  <li>Offering pills: <code>src/components/OfferingPills.jsx</code>.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Profiles</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Shared styles: <code>src/components/profile/profileStyles.ts</code>. Gallery heights are <code>GALLERY_SHORT</code> (176) and <code>GALLERY_TALL</code> (269).</li>
                  <li>Screens: <code>app/(home)/userProfile/[profileId].tsx</code> (public) and <code>app/(home)/profile/index.jsx</code> (own).</li>
                  <li>The table record row is sent only when the server switch <code>profileTableRecordEnabled</code> is on; see <code>src/controllers/userController.js</code> in the server repo.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Last Call tile</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Component: <code>src/components/LastCallShort.tsx</code>. The flyer is a borderless FlyerCard with <code>sheen={'{false}'}</code>. The label and bar are <code>LIME</code> from <code>constants/brandTokens.js</code>; the window is <code>LAST_CALL_MS</code>, 60 minutes from <code>last_call_at</code>.</li>
                  <li>Title string: <code>rowDrops.tileTitle</code>, "{'{{name}}'} at {'{{event}}'}".</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Profile header</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Scroll-driven header background: <code>src/components/AnimatedHeaderBackground/index.jsx</code>. On iOS it animates the blur strength, not opacity, because the blur ignores opacity.</li>
                  <li>Used by <code>app/(home)/userProfile/[profileId].tsx</code> and <code>app/(home)/profile/index.jsx</code>. Don't give the header a fill of its own.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">State screens</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Full-page block: <code>src/components/StateScreen/index.tsx</code>. Pass <code>tabBar</code> where the tab bar shows.</li>
                  <li>Used by <code>src/components/Connectivity/index.jsx</code> (offline), <code>src/components/PermissionDenied/index.tsx</code>, <code>src/components/ScreenshotBlockedOverlay.tsx</code>, <code>app/(auth)/verifyPhoneRequired.tsx</code>, <code>app/(auth)/loginLink.tsx</code>, and the not-found states in <code>app/(home)/userGuestList.jsx</code>, <code>app/(home)/drinks/[giftId].tsx</code> and <code>app/(home)/events/bottleService/request/[requestId].tsx</code>.</li>
                  <li>Inline Couldn't load: <code>src/components/LoadError/index.tsx</code>, used in <code>app/(home)/myZona/index.tsx</code>, <code>src/components/Chat/InboxTab.tsx</code> and <code>app/(home)/discover/index.jsx</code>.</li>
                  <li>Update banner: <code>src/components/UpdateBanner/index.tsx</code>.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Screens</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>See all headers: <code>src/components/LastCallShort.tsx</code> and <code>src/components/VenueTablesSection.jsx</code>, color <code>LINK_GO</code> from <code>src/constants/typography.ts</code>. The Recommended row (<code>src/components/RecommendedShort.jsx</code>, behind a server switch) still draws the arrow.</li>
                  <li>Host guest list, option B: <code>app/(home)/userGuestList.jsx</code> (<code>requestRow</code>, <code>requestActions</code>, <code>guestMore</code>, and the <code>OneSheet</code> opened by <code>guestMenu</code>). Strings <code>userGuestList.wantsToJoin</code>, <code>removeFromGuestList</code>, <code>guestOptions</code>.</li>
                  <li>Partner row: <code>app/(home)/events/[eventId].jsx</code>, shown when the event has <code>externalPaymentLink</code>. A venue's <code>partner_button_label</code> still overrides Details and <code>partner_row_hidden</code> hides the row; <code>partner_note</code> is no longer shown.</li>
                  <li>Plan ticket row: <code>src/components/PlanTicketRow.js</code>, used in <code>app/(home)/userGuestList.jsx</code> and <code>app/(home)/groupList.jsx</code>. It replaced <code>PlanTicketButton</code>.</li>
                  <li>Meet People grid: <code>app/(home)/discover/index.jsx</code> (<code>tileSize</code>, <code>tileHeight</code>).</li>
                  <li>Plan designer day choices: the <code>choice</code> style in <code>app/(home)/guestlist/create.jsx</code>.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Android</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Keyboard mode is pan: <code>softwareKeyboardLayoutMode: 'pan'</code> in <code>app.config.js</code>, <code>adjustPan</code> in <code>AndroidManifest.xml</code>.</li>
                  <li>Insets: <code>useBottomInset</code> and <code>useTabBarSpace</code> in <code>src/hooks/useBottomTabSpace.js</code>.</li>
                  <li>Buttons above the field while typing: <code>src/components/Chat/WinkGate.tsx</code>. Closed chat footer inset: <code>app/(home)/chat/private/[chatId].tsx</code> and <code>app/(home)/chat/group/[chatId].tsx</code>.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Radar</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>Pins and their colors: <code>src/components/PulsingMarker/PulsingMarker.tsx</code>. The radar screen: <code>app/(home)/myZona/index.tsx</code>. Both re-read on 2026-09-17.</li>
                  <li>The promoter star glyph shipped on 2026-09-09 in PR #287.</li>
                </ul>
              </div>

              <div className="bb-dev__group">
                <h3 className="bb-name">Copy</h3>
                <ul className="bb-prose bb-dev__list">
                  <li>No em dashes and no "--" is enforced on the server by <code>test/noEmDashes.test.js</code>.</li>
                </ul>
              </div>
            </div>
          </section>

          {/*  ─────────────  COLOPHON  ─────────────  */}
          <section className="bb-spread bb-colophon">
            <div className="bb-colophon__rule" />
            <div className="bb-colophon__grid">
              <div>
                <div className="bb-eyebrow">Title</div>
                <div className="bb-mono">ZONA Brand Book</div>
              </div>
              <div>
                <div className="bb-eyebrow">Volume</div>
                <div className="bb-mono">01 · {new Date().getFullYear()}</div>
              </div>
              <div>
                <div className="bb-eyebrow">Last updated</div>
                <div className="bb-mono">{LAST_UPDATED}</div>
              </div>
              <div>
                <div className="bb-eyebrow">Maintained by</div>
                <div className="bb-mono">ZONA APP CORP</div>
              </div>
            </div>
            <p className="bb-colophon__note">
              Internal reference. Anything not documented here is not the brand.
              A new glyph, color or font is not in the brand book until this page
              says so: the promoter star shipped 2026-09-09 and sat undocumented
              until 2026-09-17. Questions go to the brand team before they go to
              print.
            </p>
          </section>
      </article>
    </main>
  );
};

export default BrandBook;
